import type { MapData, TileType } from '@/types/map-data';

interface ValidationResult {
  readonly valid: boolean;
  readonly errors: ReadonlyArray<string>;
}

function countTileType(tiles: ReadonlyArray<ReadonlyArray<TileType>>, type: TileType): number {
  let count = 0;
  for (const row of tiles) {
    for (const tile of row) {
      if (tile === type) {
        count++;
      }
    }
  }
  return count;
}

function hasTileType(tiles: ReadonlyArray<ReadonlyArray<TileType>>, type: TileType): boolean {
  return countTileType(tiles, type) > 0;
}

export function validateMapData(mapData: MapData): ValidationResult {
  const errors: string[] = [];
  const mapName = mapData.name || mapData.id || 'unknown';

  // Validate dimensions
  if (mapData.width <= 0 || mapData.height <= 0) {
    errors.push(`Map "${mapName}": Invalid dimensions (${mapData.width}x${mapData.height})`);
  }

  // Validate tiles array matches declared dimensions
  if (mapData.tiles.length !== mapData.height) {
    errors.push(
      `Map "${mapName}": Tile rows (${mapData.tiles.length}) doesn't match declared height (${mapData.height})`,
    );
  }

  for (let y = 0; y < mapData.tiles.length; y++) {
    const row = mapData.tiles[y];
    if (row && row.length !== mapData.width) {
      errors.push(
        `Map "${mapName}": Row ${y} width (${row.length}) doesn't match declared width (${mapData.width})`,
      );
    }
  }

  // Validate required tile types
  if (!hasTileType(mapData.tiles, 'wall')) {
    errors.push(`Map "${mapName}": No wall tiles found`);
  }

  if (!hasTileType(mapData.tiles, 'dot')) {
    errors.push(`Map "${mapName}": No dot tiles found`);
  }

  if (!hasTileType(mapData.tiles, 'spawn_point')) {
    errors.push(`Map "${mapName}": No Pac-Man spawn point tile found`);
  }

  if (!hasTileType(mapData.tiles, 'ghost_base')) {
    errors.push(`Map "${mapName}": No ghost base tiles found`);
  }

  // Validate power pills exist (typically 4)
  const powerPillCount = countTileType(mapData.tiles, 'power_pill');
  if (powerPillCount === 0) {
    errors.push(`Map "${mapName}": No power pill tiles found`);
  }

  // Validate fruit spawn exists in tiles
  if (!hasTileType(mapData.tiles, 'fruit_spawn')) {
    errors.push(`Map "${mapName}": No fruit spawn tile found`);
  }

  // Validate tunnel tiles exist
  if (!hasTileType(mapData.tiles, 'tunnel')) {
    errors.push(`Map "${mapName}": No tunnel tiles found`);
  }

  // Validate tunnel pairs
  if (!mapData.tunnelPairs || mapData.tunnelPairs.length === 0) {
    errors.push(`Map "${mapName}": No tunnel pairs defined`);
  }

  // Validate ghost release point is within map bounds
  if (
    mapData.ghostReleasePoint.x < 0 ||
    mapData.ghostReleasePoint.x >= mapData.width ||
    mapData.ghostReleasePoint.y < 0 ||
    mapData.ghostReleasePoint.y >= mapData.height
  ) {
    errors.push(`Map "${mapName}": Ghost release point is out of bounds`);
  }

  // Validate Pac-Man spawn is within map bounds
  if (
    mapData.pacmanSpawn.x < 0 ||
    mapData.pacmanSpawn.x >= mapData.width ||
    mapData.pacmanSpawn.y < 0 ||
    mapData.pacmanSpawn.y >= mapData.height
  ) {
    errors.push(`Map "${mapName}": Pac-Man spawn point is out of bounds`);
  }

  // Validate fruit spawn is within map bounds
  if (
    mapData.fruitSpawn.x < 0 ||
    mapData.fruitSpawn.x >= mapData.width ||
    mapData.fruitSpawn.y < 0 ||
    mapData.fruitSpawn.y >= mapData.height
  ) {
    errors.push(`Map "${mapName}": Fruit spawn point is out of bounds`);
  }

  // Validate speed multiplier is positive
  if (mapData.speedMultiplier <= 0) {
    errors.push(`Map "${mapName}": Speed multiplier must be positive`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export class MapValidationError extends Error {
  readonly errors: ReadonlyArray<string>;

  constructor(errors: ReadonlyArray<string>) {
    super(`Map validation failed:\n${errors.join('\n')}`);
    this.name = 'MapValidationError';
    this.errors = errors;
  }
}

export function validateMapOrThrow(mapData: MapData): void {
  const result = validateMapData(mapData);
  if (!result.valid) {
    throw new MapValidationError(result.errors);
  }
}
